/**
 * Image utilities for handling uploads, validation, and optimization
 */

export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
export const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB

export function validateImageFile(file: File): { valid: boolean; error?: string } {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Formato no válido. Solo se permiten PNG, JPG, SVG y WebP.',
    };
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return {
      valid: false,
      error: 'Archivo demasiado grande. Máximo 2MB.',
    };
  }

  return { valid: true };
}

export function generateUniqueFileName(file: File, empresaId: string): string {
  const extension = file.name.split('.').pop() || 'png';
  const timestamp = Date.now();
  return `${empresaId}-${timestamp}.${extension}`;
}

export async function resizeImage(
  file: File,
  maxWidth: number = 500,
  maxHeight: number = 500
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          'image/webp',
          0.9
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function getImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;

  // If it's already a full URL, return it
  if (path.startsWith('http')) return path;

  // Otherwise construct the Supabase storage URL
  const SUPABASE_URL = "https://ppmfxnwbanrqgsmydzjo.supabase.co";
  return `${SUPABASE_URL}/storage/v1/object/public/empresa-logos/${path}`;
}

/**
 * Universal image compression for storage uploads.
 * - Non-image files (PDF, DOC, etc.) → returned untouched.
 * - SVG/GIF → returned untouched (vector / animation).
 * - Other images → resized so longest side ≤ maxDimension and re-encoded to WebP.
 * Always returns a File (with `.webp` extension when re-encoded).
 */
export interface CompressOptions {
  maxDimension?: number;
  quality?: number;
}

export async function compressImageForUpload(
  file: File,
  { maxDimension = 1200, quality = 0.82 }: CompressOptions = {}
): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/svg+xml') return file;
  if (file.type === 'image/gif') return file;

  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('read fail'));
      reader.readAsDataURL(file);
    });

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('img fail'));
      i.src = dataUrl;
    });

    let width = img.width;
    let height = img.height;
    if (width > maxDimension || height > maxDimension) {
      if (width >= height) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      } else {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/webp', quality)
    );
    if (!blob) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn('[compressImageForUpload] fallback to original:', err);
    return file;
  }
}

